import { useState } from 'react';
import { Skeleton } from '../../components/Skeleton';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Section } from '../../components/Section';
import { AliasManager } from '../../components/ingredients/AliasManager';
import { AllergenCorrections } from '../../components/ingredients/AllergenCorrections';
import { ImproveEntryForm } from '../../components/ingredients/ImproveEntryForm';
import { MergeDialog } from '../../components/ingredients/MergeDialog';
import { SharedPhotos } from '../../components/ingredients/SharedPhotos';
import { api, queryKeys } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { messageForError } from '../../lib/errors';
import { categoryLabel, shelfLifeLabel } from '../../lib/ingredientDisplay';
import { unitLabel } from '../../lib/units';

export function IngredientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { auth } = useAuth();
  const isAdmin = auth.status === 'authed' && auth.user.isAdmin;
  const [mergeOpen, setMergeOpen] = useState(false);

  const ingredient = useQuery({
    queryKey: queryKeys.ingredientDetail(id ?? ''),
    queryFn: () => api.ingredients.get(id!),
    enabled: Boolean(id),
  });

  if (!id) return null;

  return (
    <AppShell title={ingredient.data?.canonicalName ?? 'Ingredient'}>
      <div className="flex flex-col gap-4">
        {ingredient.isPending ? <Skeleton lines={3} /> : null}
        {ingredient.isError ? (
          <Alert tone="error">{messageForError(ingredient.error)}</Alert>
        ) : null}

        {ingredient.data ? (
          <>
            <Section title="Overview">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted">Category</dt>
                <dd className="text-ink">{categoryLabel(ingredient.data.category)}</dd>
                <dt className="text-muted">Default unit</dt>
                <dd className="text-ink">{unitLabel(ingredient.data.defaultUnit)}</dd>
                <dt className="text-muted">Density</dt>
                <dd className="text-ink">
                  {ingredient.data.densityGPerMl != null
                    ? `${ingredient.data.densityGPerMl} g/ml`
                    : 'Unknown'}
                </dd>
                <dt className="text-muted">Weight of one</dt>
                <dd className="text-ink">
                  {ingredient.data.avgUnitWeightG != null
                    ? `${ingredient.data.avgUnitWeightG} g`
                    : 'Unknown'}
                </dd>
              </dl>
              <div>
                <p className="mb-1 text-sm font-medium text-ink">Shelf life</p>
                <dl className="grid grid-cols-3 gap-2 text-sm">
                  <div className="rounded-lg border border-line p-2 text-center">
                    <dt className="text-xs text-muted">Pantry</dt>
                    <dd className="text-ink">
                      {shelfLifeLabel(ingredient.data.shelfLifeDays.pantry)}
                    </dd>
                  </div>
                  <div className="rounded-lg border border-line p-2 text-center">
                    <dt className="text-xs text-muted">Fridge</dt>
                    <dd className="text-ink">
                      {shelfLifeLabel(ingredient.data.shelfLifeDays.fridge)}
                    </dd>
                  </div>
                  <div className="rounded-lg border border-line p-2 text-center">
                    <dt className="text-xs text-muted">Freezer</dt>
                    <dd className="text-ink">
                      {shelfLifeLabel(ingredient.data.shelfLifeDays.freezer)}
                    </dd>
                  </div>
                </dl>
              </div>
            </Section>

            <Section
              title="Allergens"
              description="What the planner checks against your family's allergies."
            >
              <AllergenCorrections ingredient={ingredient.data} />
            </Section>

            <Section
              title="Also known as"
              description="Alternate names used to find this ingredient."
            >
              <AliasManager
                ingredientId={ingredient.data.id}
                aliases={ingredient.data.aliases}
                isAdmin={isAdmin}
              />
            </Section>

            <Section
              title="Shelf photos"
              description="Shared by shoppers so others can spot it on the shelf."
            >
              <SharedPhotos
                ingredientId={ingredient.data.id}
                name={ingredient.data.canonicalName}
              />
            </Section>

            <Section title="Improve this entry">
              <ImproveEntryForm ingredient={ingredient.data} isAdmin={isAdmin} />
            </Section>

            {isAdmin ? (
              <Section
                title="Merge"
                description="Fold a duplicate ingredient into this one, or this one into another."
              >
                <Button variant="secondary" onClick={() => setMergeOpen(true)}>
                  Merge into another ingredient…
                </Button>
              </Section>
            ) : null}

            <MergeDialog
              ingredient={ingredient.data}
              open={mergeOpen}
              onClose={() => setMergeOpen(false)}
            />
          </>
        ) : null}
      </div>
    </AppShell>
  );
}
